import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import api from "../../api";
import {
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Chip
} from "@mui/material";
import {
  Squares2X2Icon,
  UsersIcon,
  PresentationChartLineIcon,
  BuildingStorefrontIcon,
  PencilSquareIcon
} from "@heroicons/react/24/outline";
import { DE, ES, FR, GB } from 'country-flag-icons/react/3x2';
import ListingImageGallery from "components/ListingImageGallery/ListingImageGallery";
import LikeSaveBtns from "components/LikeSaveBtns";
import './style.css';
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { setAgency } from "../../redux/reducers/agencyReducer";
import Badge from "shared/Badge/Badge";

const styles = {
  langflag: {
    width: '24px',
    margin: '0 4px'
  },
  sbTitle: {
    fontWeight: 'bold',
    marginRight: '12px'
  }
};

const countryFlags = {
  "DE": <DE key="lng-1" title="German" className="langflag" />,
  "ES": <ES key="lng-2" title="Spanish" className="langflag" />,
  "FR": <FR key="lng-3" title="French" className="langflag" />,
  "GB": <GB key="lng-4" title="English" className="langflag" />,
};

const availabilityAlerts = [
  <></>,
  <Alert severity="success">Open for new brand partnerships</Alert>,
  <Alert severity="warning">Quite busy but open for selected brands</Alert>,
  <Alert severity="error">Fully booked! no requests at this time</Alert>,
];

const SimpleAgencyProfile = ({ user }) => {
  const dispatch = useDispatch();
  const { shortname } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const thisPathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const modal = searchParams?.get("modal");

  const [agencyDetail, setDetailAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiCallMade, setApiCallMade] = useState(false);
  const [isContactRequested, setIsContactRequested] = useState(false);
  const [sbState, setSBState] = useState({ open: false });
  const [isMounted, setIsMounted] = useState(false);
  const [pageLabel, setPageLabel] = useState('');

  useEffect(() => {
    localStorage.setItem('last_page', '/agency/' + shortname);
    if (user?.id && thisPathname.includes("view")) {
      window.location.href = '/agency/' + shortname;
    }
  }, [])

  useEffect(() => {
    if (apiCallMade || !shortname) return;

    setApiCallMade(true);
    setLoading(true);

    console.log(`Fetching agency data for: ${shortname} with sessionUser: ${user?.id || 251}`);

    if (user?.id) {
      api.getAgency(
        {
          shortname: shortname,
          sessionUser: user?.id || 251
        },
        (res) => {
          console.log("API response received");
          if (res && res.data) {
            console.log("Setting agency data");
            const agencyData = { ...res.data };
            agencyData.imageUrls = agencyData.images?.map(img =>
              `${api.getUploadUrl()}/${img.url}`
            ) || [];

            setDetailAgency(agencyData);
            dispatch(setAgency(agencyData));
            setLoading(false);
          } else {
            console.log("No data in API response");
            setError("No agency data found");
            setLoading(false);
          }
        }
      );
    } else {
      // fetch agency data for public view
      api.getViewAgency(
        {
          shortname: shortname,
        },
        (res) => {
          if (res && res.data) {
            const agencyData = { ...res.data };
            agencyData.imageUrls = agencyData.images?.map(img =>
              `${api.getUploadUrl()}/${img.url}`
            ) || [];

            setDetailAgency(agencyData);
            dispatch(setAgency(agencyData));
            setLoading(false);
          } else {
            setError("No agency data found");
            setLoading(false);
          }
        }
      );
    }

    const timeout = setTimeout(() => {
      if (loading) {
        console.log("Safety timeout triggered");
        setLoading(false);
        setError("Request timed out. Please try again later.");
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [shortname, user?.id, apiCallMade, loading]);

  const handleOpenModalImageGallery = () => {
    navigate(`${location.pathname}?modal=PHOTO_TOUR_SCROLLABLE`);
  };

  const handleOpenModalVideoGallery = () => {
    navigate(`${location.pathname}?modal=VIDEO_FOCUS`);
    const params = new URLSearchParams(document.location.search);
    params.set("photoId", "0");
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const getNewParam = ({
    paramName = "photoId",
    value,
  }) => {
    let params = new URLSearchParams(document.location.search);
    params.set(paramName, String(value));
    return params.toString();
  };

  const handleCloseModalImageGallery = () => {
    const params = new URLSearchParams(document.location.search);
    params.delete("modal");
    params.delete("photoId");
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleContactRequest = () => {
    api.requestContact({
      requester_type: user?.company_type,
      requester_id: user?.company?.id,
      contact_type: 'agencies',
      contact_id: agencyDetail?.id,
    }, (res) => {
      openSuccessSnackbar({ vertical: 'bottom', horizontal: 'center' });
      setIsContactRequested(true);
    });
  };

  const openSuccessSnackbar = (position) => {
    setSBState({ ...position, open: true });
  };

  const handleCloseSB = () => {
    setSBState({ ...sbState, open: false });
  };

  const getRegions = () => {
    if (!agencyDetail?.regions) return "";
    return agencyDetail.regions.map(region => region.region).join(", ");
  };

  const getProductCategories = () => {
    if (!agencyDetail?.categories) return "";
    return agencyDetail.categories.map(c => c.name).join(", ");
  };

  const getBrandSummary = () => {
    if (!agencyDetail?.brand_experience?.current_brands) return "";
    return agencyDetail.brand_experience.current_brands
      .map(r => r.name)
      .join(", ");
  };

  const getTargetGroups = () => {
    if (!agencyDetail?.market?.target_groups) return "";
    return agencyDetail.market.target_groups.join(", ");
  };

  const getPriceRange = () => {
    if (!agencyDetail?.sales_details?.priceRange) return "€0";
    const prices = agencyDetail.sales_details.priceRange.split("-");
    return `€${prices[0]}-€${prices[1]}`;
  };

  const goToSettings = () => {
    navigate('/account');
  };

  const handleEdit = (type) => {
    navigate('/account?page=' + type);
  }

  const renderEditButton = (type) => {
    return (
      <>
        {user?.company?.id === agencyDetail?.id && (
          <button
            type="button"
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ml-2"
            aria-label="Edit sales channel"
            onClick={() => handleEdit(type)}
          >
            <PencilSquareIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        )}
      </>
    )
  }
  useEffect(() => {
    setPageLabel('Agency');
  }, []);

  const renderSection1 = () => {
    if (!agencyDetail) return null;

    return (
      <div className="listingSection__wrap !space-y-6">
        {/* Add Badge here */}
        <div className="flex justify-between items-center">
          <Badge name={pageLabel} />
          {user.id !== 0 && user.id !== null && (
            <LikeSaveBtns />
          )}
        </div>

        <div className="flex">
          <>
            <h2 className="text-2xl sm:text-3xl lg:text-3xl font-semibold flex-1">
              {agencyDetail?.name}
              {renderEditButton('about')}
            </h2>
          </>
        </div>

        <div className="items-center">
          <div className="flex">
            <BuildingStorefrontIcon className="w-5 h-5" />
            <span className="ml-4"> Showrooms</span>
            {renderEditButton('about')}
          </div>
          <Stack className="ml-8">
            {agencyDetail.showrooms && agencyDetail.showrooms.map((s, i) => (
              <span key={i}>
                <i className="las la-map-marker-alt"></i>
                <span className="ml-3">{s}</span>
              </span>
            ))}
          </Stack>
        </div>

        <div className="flex">
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          {renderEditButton('business-details')}
        </div>
        <div className="text-neutral-6000 dark:text-neutral-300 whitespace-pre-line">
          {agencyDetail?.description}
        </div>
      </div>
    );
  };

  const renderSection2 = () => {
    if (!agencyDetail) return null;

    return (
      <div className="listingSection__wrap">
        <div>
          <h2 className="text-2xl font-semibold">Vision & Goals{renderEditButton('about')}</h2>
          <div>
            <h3 className='font-semibold mt-5 ml-3'>What we look for in a Brand Partnership</h3>
            <p className="mt-3 ml-6 whitespace-pre-line">{agencyDetail.vg_brand_partnership}</p>

            <h3 className='font-semibold mt-5 ml-3'>Long Term Goals in Sales</h3>
            <p className="mt-3 ml-6 whitespace-pre-line">{agencyDetail.vg_sales}</p>

            <h3 className='font-semibold mt-5 ml-3'>Strengths</h3>
            <p className="mt-3 ml-6 whitespace-pre-line">{agencyDetail.vg_strengths}</p>
          </div>
        </div>

        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <div>
          <h2 className="text-2xl font-semibold">How we Work{renderEditButton('how-you-work')}</h2>
          <div className="flex">
            <div className="flex-1 m-3">
              <div className="flex">
                <div className="mr-5">
                  <PresentationChartLineIcon className="mt-2 w-8 h-8" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold">Preferred Sales Strategy</span>
                  <p>{agencyDetail.sales_strat}</p>
                </div>
              </div>
            </div>
            <div className="flex-1 m-3">
              <div className="flex">
                <div className="mr-5">
                  <UsersIcon className="mt-2 w-8 h-8" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold">B2B Network Size</span>
                  <p>{agencyDetail.b2b_size} Retailers</p>
                </div>
              </div>
            </div>
          </div>
          <Stack>
            <p className="m-3">
              {agencyDetail.trade_fairs}
            </p>
          </Stack>
        </div>

        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <h2 id='brand-experience' className="text-2xl font-semibold">Brand Experience{renderEditButton('brand-experience')}</h2>
        <div className="m-3">
          <h3 className="text-1xl font-semibold mb-5">Current Brands</h3>
          {agencyDetail?.brand_experience?.current_brands && (
            agencyDetail.brand_experience.current_brands.map((brand, i) => (
              <div key={`bec-${i}`}>
                <h3 className="font-semibold mb-2">{brand.name}</h3>
                <p className="ml-3 mb-5 whitespace-pre-line">{brand.achievements}</p>
                <p className="ml-3 mb-5 italic">
                  Sold to {brand.sales?.num_dealers ?? '--'} dealers at a price range of {brand.sales?.price_range ?? '--'}
                </p>
              </div>
            ))
          )}

          <h3 className="text-1xl font-semibold mb-5">Past Brands</h3>
          {agencyDetail?.brand_experience?.past_brands?.length > 0 && (
            agencyDetail.brand_experience.past_brands.map((brand, i) => (
              <div key={`bec-${i}`}>
                <h3 className="font-semibold mb-2">{brand.name}</h3>
                <p className="ml-3 mb-5 whitespace-pre-line">{brand.achievements}</p>
                <p className="ml-3 mb-5 italic">
                  Sold to {brand.sales?.num_dealers ?? '--'} dealers at a price range of {brand.sales?.price_range ?? '--'}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderSidebar = () => {
    if (!agencyDetail) return null;

    return (
      <div className="listingSectionSidebar__wrap shadow-xl">
        {agencyDetail.availability_status && availabilityAlerts[agencyDetail.availability_status]}

        <div className="flex flex-col space-y-4">
          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Office Address</span>
            <span className="flex-1">
              <i className="las la-map-marker-alt"></i>
              <span className="ml-1">
                {`${agencyDetail?.street_address} ${agencyDetail?.post} ${agencyDetail?.city}, ${agencyDetail?.country}`}
              </span>
            </span>
            {renderEditButton('business-details')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Languages</span>
            <Stack direction={'row'} className="flex-1">
              {agencyDetail?.about?.languages &&
                agencyDetail.about.languages.map((language) => countryFlags[language])}
            </Stack>
            {renderEditButton('about')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Regions</span>
            <span className="flex-1">{getRegions()}</span>
            {renderEditButton('markets')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Industry Experience</span>
            <span className="flex-1">{agencyDetail.about?.industry_experience ?? '--'} Jaar</span>
            {renderEditButton('about')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Categories</span>
            <span className="flex-1">{getProductCategories()}</span>
            {renderEditButton('markets')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Target Groups</span>
            <span className="flex-1">{getTargetGroups()}</span>
            {renderEditButton('markets')}
          </div>

          <div className="flex">
            <span style={styles.sbTitle} className="flex-1">Brands</span>
            <Stack className="flex-1">
              <span>{getBrandSummary()}</span>
              <a href='#brand-experience'>(See All)</a>
            </Stack>
            {renderEditButton('brand-experience')}
          </div>

          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
        </div>
        {user.id !== 0 && user.id !== null && (
          <ButtonPrimary href={"#"} onClick={handleContactRequest}>Request Contact</ButtonPrimary>
        )}

        {/* Button section */}
        {/* {user?.company?.id !== agency?.id ? (
          !isContactRequested ? (
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full"
              onClick={handleContactRequest}
            >
              Request Contact
            </button>
          ) : (
            <button 
              className="bg-gray-400 text-white font-bold py-2 px-4 rounded w-full"
              disabled
            >
              Contact Requested
            </button>
          )
        ) : (
          <button 
            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded w-full"
            onClick={goToSettings}
          >
            Edit Profile
          </button>
        )} */}
      </div>
    );
  };

  const renderHeader = () => {
    if (!agencyDetail?.showroom_video?.length && !agencyDetail?.imageUrls?.length) return null;

    return (
      <header className="rounded-md sm:rounded-xl">
        <div className="relative grid grid-cols-3 sm:grid-cols-4 gap-1 sm:gap-2">
          <div
            className="col-span-2 row-span-3 sm:row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer  min-h-[400px]"
            onClick={
              agencyDetail.showroom_video && agencyDetail.showroom_video.length > 0
                ? handleOpenModalVideoGallery
                : handleOpenModalImageGallery
            }
          >
            {agencyDetail.showroom_video && agencyDetail.showroom_video.length > 0 ? (
              <video
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                autoPlay muted loop
                src={`${api.getUploadUrl()}/${agencyDetail?.showroom_video[0]?.url}`}
              />
            ) : (
              <img
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                src={agencyDetail?.imageUrls[0] ?? ''}
                alt="Agency main"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              />
            )}
            <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
          </div>

          {agencyDetail?.imageUrls
            .filter((_, i) => i >= 1 && i < 5)
            .map((item, index) => (
              <div
                key={index}
                className={`relative rounded-md sm:rounded-xl overflow-hidden ${index >= 3 ? "hidden sm:block" : ""
                  }`}
              >
                <div className="aspect-w-4 aspect-h-3 sm:aspect-w-6 sm:aspect-h-5">
                  <img
                    className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                    src={item || ""}
                    alt={`Gallery image ${index + 1}`}
                    sizes="400px"
                  />
                </div>

                <div
                  className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={handleOpenModalImageGallery}
                />
              </div>
            ))}

          <button
            className="absolute hidden md:flex md:items-center md:justify-center left-3 bottom-3 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-500 hover:bg-neutral-200 z-10"
            onClick={handleOpenModalImageGallery}
          >
            <Squares2X2Icon className="w-5 h-5" />
            <span className="ml-2 text-neutral-800 text-sm font-medium">
              Show all photos
            </span>
          </button>
        </div>
      </header>
    );
  };

  const getImageGalleryListing = () => {
    if (!agencyDetail?.images) return [];
    return agencyDetail.images.map((img, id) => ({
      id,
      url: `${api.getUploadUrl()}/${img.url}`,
      caption: img.caption
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <CircularProgress />
          <p className="mt-4">Loading agency details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (!agencyDetail) {
    return (
      <div className="p-8">
        <Alert severity="info">No agency information available.</Alert>
      </div>
    );
  }

  return (
    <div className="nc-ListingStayDetailPage">
      {user?.company?.id === agencyDetail?.id && (
        <Alert severity="info" className="sticky top-[100px] justify-center mb-4 z-20">
          Currently viewing agency profile as a brand would see it
        </Alert>
      )}

      <ListingImageGallery
        isShowModal={modal === "PHOTO_TOUR_SCROLLABLE"}
        onClose={handleCloseModalImageGallery}
        images={getImageGalleryListing()}
      />

      {agencyDetail.showroom_video && agencyDetail.showroom_video.length > 0 && (
        <ListingImageGallery
          isShowModal={modal === "VIDEO_FOCUS"}
          onClose={handleCloseModalImageGallery}
          images={[{ id: 1, url: `${api.getUploadUrl()}/${agencyDetail?.showroom_video[0]?.url}` }]}
        />
      )}

      <div className="container ListingDetailPage__content">
        {renderHeader()}

        <main className="relative z-10 mt-11 flex flex-col lg:flex-row">
          <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pr-10">
            {renderSection1()}
            {renderSection2()}
          </div>

          <div className="hidden lg:block flex-grow mt-14 lg:mt-0">
            <div className={`sticky ${user?.company?.id === agencyDetail?.id ? 'top-[170px]' : 'top-28'}`}>
              {renderSidebar()}
            </div>
          </div>
        </main>

        <div className="container py-24 lg:py-32">
          <div className="relative py-16">
            {/* <BackgroundSection /> */}
          </div>
        </div>

        <div className="block lg:hidden fixed bottom-0 inset-x-0 py-2 sm:py-3 bg-white dark:bg-neutral-800 shadow-lg border-t border-neutral-200 dark:border-neutral-700 z-20">
          <div className="container flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-semibold">
                {agencyDetail?.name}
              </span>
            </div>

            <ButtonPrimary href={"#"} onClick={handleContactRequest}>Request Contact</ButtonPrimary>

            {/* {user?.company?.id !== agencyDetail?.id ? (
              !isContactRequested ? (
                <button 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg"
                  onClick={handleContactRequest}
                >
                  Request Contact
                </button>
              ) : (
                <button 
                  className="bg-gray-400 text-white font-medium py-2 px-4 rounded-lg"
                  disabled
                >
                  Contact Requested
                </button>
              )
            ) : (
              <button 
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg"
                onClick={goToSettings}
              >
                Edit Profile
              </button>
            )} */}
          </div>
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={sbState.open}
        autoHideDuration={5000}
        onClose={handleCloseSB}
        message="Contact Requested"
      >
        <Alert
          onClose={handleCloseSB}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Contact Request Sent!
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  agency: state.agency,
});

export default connect(mapStateToProps, { setAgency })(SimpleAgencyProfile);